import {
  clear as clearIdbStore,
  STORE_ARTIFACT_BLOBS,
  STORE_PORTFOLIO_ARTIFACTS,
  STORE_WAL_BLOBS,
  STORE_WAL_QUEUE,
} from './idb';
import { clearCachedSubscription } from './sub-cache';
import { clearAllPhotos } from '../utils/photo-db';

const USER_STORAGE_PREFIX = 'homeschool-';
const PRESERVED_LOCAL_KEYS = new Set([
  'homeschool-offline',
]);

// Persisted stores that hold per-account data but whose localStorage key does
// NOT follow the `homeschool-` convention, so the prefix sweep below misses
// them. Without this they bleed across accounts on a shared browser. New stores
// should use the `homeschool-` prefix; anything that can't goes here.
const EXTRA_USER_SCOPED_KEYS = [
  'pe-quest-store', // src/store/peQuestStore.ts — per-child PE quest completions
];

export const ACTIVE_USER_STORAGE_KEY = 'hsy.active-user-id';

export function getLastActiveUserId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(ACTIVE_USER_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setLastActiveUserId(userId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (userId) localStorage.setItem(ACTIVE_USER_STORAGE_KEY, userId);
    else localStorage.removeItem(ACTIVE_USER_STORAGE_KEY);
  } catch {
    // localStorage may be unavailable in private mode.
  }
}

function clearUserScopedLocalStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (!key.startsWith(USER_STORAGE_PREFIX)) continue;
      if (PRESERVED_LOCAL_KEYS.has(key)) continue;
      keysToRemove.push(key);
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
    for (const key of EXTRA_USER_SCOPED_KEYS) {
      if (!PRESERVED_LOCAL_KEYS.has(key)) localStorage.removeItem(key);
    }
  } catch {
    // localStorage may be blocked; do the best we can.
  }
}

export async function clearClientFamilyData(): Promise<void> {
  clearUserScopedLocalStorage();
  clearCachedSubscription();
  const { clearSyncKey } = await import('./sync-crypto');

  await Promise.allSettled([
    clearSyncKey(),
    clearAllPhotos(),
    clearIdbStore(STORE_PORTFOLIO_ARTIFACTS),
    clearIdbStore(STORE_ARTIFACT_BLOBS),
    clearIdbStore(STORE_WAL_QUEUE),
    clearIdbStore(STORE_WAL_BLOBS),
  ]);
}
